import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Check, Clock, Trash2, Calendar } from "lucide-react";

const STATUSES = [
  { k: "todo", label: "To-do", color: "text-white/60" },
  { k: "in_progress", label: "In progress", color: "text-secondary" },
  { k: "done", label: "Done", color: "text-primary" },
];

const CATEGORIES = ["career", "release", "audience", "business", "craft"];

const SUGGESTIONS = [
  { title: "First brand collaboration", category: "business" },
  { title: "First gig outside my city", category: "career" },
  { title: "10K monthly listeners", category: "audience" },
  { title: "Release a record", category: "release" },
  { title: "Solo show / exhibition", category: "career" },
  { title: "Press feature", category: "audience" },
];

export default function MilestonesSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", target_date: "", category: "career", status: "todo" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/milestones"); setItems(data.items); }
    catch { toast.error("Failed to load milestones"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      await api.post("/milestones", form);
      toast.success("Milestone added");
      setForm({ title: "", description: "", target_date: "", category: "career", status: "todo" });
      setShowForm(false);
      load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/milestones/${id}`, { status }); load(); }
    catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this milestone?")) return;
    try { await api.delete(`/milestones/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const grouped = STATUSES.map((s) => ({ ...s, items: items.filter((i) => i.status === s.k) }));

  return (
    <div>
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="meta-mono">— MILESTONES · {items.length} TRACKED</p>
          <p className="text-white/65 text-sm mt-1 max-w-xl">Personal goals. Set targets, check them off, watch the journey stack up.</p>
        </div>
        <button data-testid="milestone-add" onClick={() => setShowForm(!showForm)} className="btn-pill btn-coral"><Plus size={14} /> {showForm ? "Cancel" : "New milestone"}</button>
      </div>

      {showForm && (
        <div className="border border-border p-5 mb-8 grid md:grid-cols-2 gap-px bg-border">
          <div className="bg-background p-5">
            <p className="meta-mono mb-2">— TITLE</p>
            <input data-testid="milestone-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. First brand collaboration" className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-2 text-lg font-light" />
            <div className="mt-4 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s.title} onClick={() => setForm({ ...form, title: s.title, category: s.category })} className="px-3 py-1 border border-border text-[10px] font-mono uppercase tracking-wider hover:border-primary hover:text-primary">{s.title}</button>
              ))}
            </div>
          </div>
          <div className="bg-background p-5">
            <p className="meta-mono mb-2">— TARGET DATE</p>
            <input data-testid="milestone-date" type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-2 text-lg font-light" />
          </div>
          <div className="bg-background p-5">
            <p className="meta-mono mb-2">— CATEGORY</p>
            <select data-testid="milestone-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-2 text-lg font-light">
              {CATEGORIES.map((c) => <option key={c} value={c} className="bg-background">{c}</option>)}
            </select>
          </div>
          <div className="bg-background p-5">
            <p className="meta-mono mb-2">— STATUS</p>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-2 text-lg font-light">
              {STATUSES.map((s) => <option key={s.k} value={s.k} className="bg-background">{s.label}</option>)}
            </select>
          </div>
          <div className="bg-background p-5 md:col-span-2">
            <p className="meta-mono mb-2">— NOTE (optional)</p>
            <textarea data-testid="milestone-note" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-2" />
          </div>
          <div className="bg-background p-5 md:col-span-2 flex justify-end">
            <button data-testid="milestone-save" onClick={create} disabled={saving} className="btn-pill btn-coral">{saving ? "Saving…" : "Save milestone"}</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="font-mono text-sm text-white/60">Loading…</p>
      ) : items.length === 0 ? (
        <div className="border border-border p-12 text-center">
          <p className="font-display text-2xl font-bold mb-2">Nothing tracked <span className="hl-cyan">yet.</span></p>
          <p className="text-white/65 text-sm">Drop your first goal above. Pick from the suggestions or write your own.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-px bg-border">
          {grouped.map((col) => (
            <div key={col.k} className="bg-background p-5 min-h-[320px]">
              <div className="flex items-center justify-between mb-4">
                <p className={`meta-mono ${col.color === "text-primary" ? "!text-primary" : col.color === "text-secondary" ? "!text-secondary" : ""}`}>— {col.label.toUpperCase()}</p>
                <span className="font-mono text-xs text-white/40">{col.items.length}</span>
              </div>
              <div className="space-y-3">
                {col.items.map((m) => (
                  <div key={m.milestone_id} data-testid={`milestone-${m.milestone_id}`} className="border border-border p-4 hover:bg-white/[0.02] transition">
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-display text-lg font-bold leading-tight flex-1">{m.title}</p>
                      <button onClick={() => remove(m.milestone_id)} className="text-white/40 hover:text-primary"><Trash2 size={13} /></button>
                    </div>
                    {m.description && <p className="text-white/70 text-sm mt-2">{m.description}</p>}
                    <div className="flex items-center justify-between mt-3 gap-2">
                      <span className="meta-mono !text-white/55">{m.category}{m.target_date && <span className="ml-2"><Calendar size={9} className="inline mb-px" /> {m.target_date}</span>}</span>
                      <div className="flex gap-1">
                        {STATUSES.filter((s) => s.k !== m.status).map((s) => (
                          <button key={s.k} onClick={() => updateStatus(m.milestone_id, s.k)} className="px-2 py-1 border border-border text-[9px] font-mono uppercase tracking-wider hover:border-primary hover:text-primary">→ {s.label}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
